/**
 * WorkspaceSwitcher — shadcn/ui dropdown replacing the legacy domain picker.
 *
 * Searchable single-select: clicking a workspace switches to it. The Content
 * Planner is scoped to this single selected workspace (it previously offered a
 * multi-select that blended several tenants' content into one feed — removed
 * because it leaked other tenants' keywords into the selected workspace's view).
 */

import { cmdkFuzzyFilter } from '@/lib/fuzzyMatch';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, LogOut, Plus, Search } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { activeTenantIdFromSelection, useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useTenantStore } from '@/stores/useTenantStore';
import { useClientCapabilities } from '@/hooks/useClientCapabilities';
import { setLastActiveWorkspace } from '@/services/authApi';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

export default function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  // In the ads section a workspace without a Google Ads customer id is still
  // selectable (the ads pages explain it); it is ranked below the linked ones
  // and carries a quiet "No Google Ads" tag.
  const onAdsRoute = location.pathname.startsWith('/ads-tools');
  const [searchOpen, setSearchOpen] = useState(false);
  const [workspaceQuery, setWorkspaceQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    workspaces,
    currentWorkspace,
    setCurrentWorkspace,
    selection,
  } = useWorkspaceStore();
  const activeClientLabel = currentWorkspace?.name ?? activeTenantIdFromSelection(selection);
  // The ONE capability model decides the ACTIVE row's tag; other rows use the
  // server-derived list flag (display only).
  const tenantId = useTenantStore((s) => s.currentTenant);
  const { capabilities } = useClientCapabilities(tenantId);

  const rankedWorkspaces = useMemo(() => {
    // Offboarded clients drop out of the picker (Gracie, 2026-09-10). Older
    // payloads with no tenant_status keep the current "active" default via the
    // type, so a client without the field never disappears. If the operator is
    // *currently* on an offboarded workspace the trigger label still shows it,
    // and their own membership list gives them a way out.
    //
    // Ads section: a workspace with no website has no tenant, so no ads page can
    // work for it (and the dedupe cannot collapse same-named ones). Not listed
    // here; every other route still lists it.
    return workspaces
      .filter((ws) => ws.tenant_status !== 'offboarded')
      .filter((ws) => !onAdsRoute || ws.website?.trim())
      .sort((a, b) => {
      // Ads section: linked clients first. Ranked, never hidden, so an operator
      // who cannot find a client sees it exists and is not linked.
      if (onAdsRoute && Boolean(a.has_google_ads) !== Boolean(b.has_google_ads)) {
        return a.has_google_ads ? -1 : 1;
      }
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [workspaces, onAdsRoute]);
  // Counts only the offboarded clients themselves, not rows hidden for another reason
  // (for example the no-website rows hidden in the ads section).
  const hiddenOffboarded = workspaces.filter((ws) => ws.tenant_status === 'offboarded').length;

  useEffect(() => {
    if (!searchOpen) return;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [searchOpen]);

  function handleSingleSwitch(ws: (typeof workspaces)[0]) {
    setCurrentWorkspace(ws);
    // Remember this choice so a fresh load restores it instead of defaulting to
    // the first membership alphabetically (#1262). Best-effort, fire-and-forget.
    void setLastActiveWorkspace(ws.id).catch(() => {});
    setWorkspaceQuery('');
    setSearchOpen(false);
  }

  const handleSignOut = async () => {
    setSearchOpen(false);
    await signOut();
    // Clear the active-client selection first: persist writes null to BOTH
    // this tab's session value and the localStorage mirror, so the next
    // login never re-seeds the previous user's client. reset() then clears
    // the session-scoped workspace list and profile.
    useWorkspaceStore.getState().clearSelection();
    useWorkspaceStore.getState().reset();
    if (DISABLE_AUTH) {
      navigate('/login?signed_out=1', { replace: true });
      return;
    }
    navigate('/login', { replace: true });
  };

  return (
    <Popover
      open={searchOpen}
      onOpenChange={(open) => {
        setSearchOpen(open);
        if (!open) {
          setWorkspaceQuery('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            // ⚠ A fixed 190px on a 390px screen leaves nothing for the title
            // and the remaining controls, which is how this ended up clipped
            // off the right edge. On a phone it takes the room that is left
            // and truncates the workspace name; 44px tall for a thumb.
            // Full width wherever it is placed on a phone (it now lives inside
            // the grouped sheet); the original fixed width returns at md.
            'flex h-11 w-full min-w-0 items-center gap-2 rounded-md border border-border/50 bg-background px-3 text-sm transition-colors md:h-9 md:w-[190px]',
            'hover:bg-muted/30 focus:border-coral/50 focus:bg-card focus:outline-none focus:ring-2 focus:ring-coral/10 md:w-[250px] xl:w-[320px]',
          )}
          aria-label={
            activeClientLabel
              ? `Current workspace: ${activeClientLabel}. Change workspace`
              : 'Select a workspace'
          }
        >
          {/* ⚠ NOT a magnifying glass. This button DISPLAYS the current
              workspace and opens a picker; the search box lives inside that
              picker. A search icon on a control showing "Bei's Plumbing" reads
              as "search for Bei's Plumbing" and is simply the wrong object —
              reported from a phone, where the label is truncated and the icon
              carries most of the meaning. */}
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-left font-medium text-foreground">
            {activeClientLabel ?? 'Select workspace'}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0">
        {/* Fuzzy search, best match first ("mke" finds mikehammermoving.com;
            lib/fuzzyMatch). cmdk ranks by the score the filter returns. */}
        <Command filter={cmdkFuzzyFilter}>
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <CommandInput
              ref={searchInputRef}
              value={workspaceQuery}
              onValueChange={(value) => {
                setWorkspaceQuery(value);
                setSearchOpen(true);
              }}
              placeholder="Search workspace"
              className="h-9 border-0 bg-transparent px-0 text-sm outline-none focus:ring-0"
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setSearchOpen(false);
                  setWorkspaceQuery('');
                }
              }}
            />
          </div>
          <CommandList className="max-h-[320px] overflow-y-auto">
            <CommandEmpty>
              {workspaces.length === 0 ? 'No workspaces yet' : 'No matching workspaces'}
            </CommandEmpty>
            {hiddenOffboarded > 0 ? (
              <div className="px-3 pb-1 pt-2 text-[11px] text-muted-foreground">
                {hiddenOffboarded} offboarded client{hiddenOffboarded === 1 ? '' : 's'} hidden. Change status from{' '}
                <a href="/clients" className="text-coral underline underline-offset-2">/clients</a>.
              </div>
            ) : null}
            <CommandGroup heading="Workspaces">
              {rankedWorkspaces.map((ws) => {
                const isCurrentSingle = ws.id === currentWorkspace?.id;
                // Active row: the verified capability object when we have it,
                // else the current workspace's list flag. Other rows: the
                // list flag.
                const hasNoGoogleAds = isCurrentSingle
                  ? capabilities
                    ? !capabilities.googleAds.customerMapped
                    : !currentWorkspace?.has_google_ads
                  : !ws.has_google_ads;
                return (
                  <CommandItem
                    key={ws.id}
                    value={`${ws.name} ${ws.website ?? ''} ${ws.slug ?? ''}`}
                    keywords={[ws.name, ws.website ?? '', ws.slug ?? ''].filter(Boolean)}
                    onSelect={() => handleSingleSwitch(ws)}
                    className={cn(
                      'flex items-center gap-2 rounded-sm px-2 py-2 text-sm',
                      !ws.is_active && 'opacity-60',
                    )}
                  >
                    <Building2 className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block truncate font-medium">{ws.name}</span>
                      {ws.website && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {ws.website}
                        </span>
                      )}
                    </span>
                    {onAdsRoute && hasNoGoogleAds ? (
                      <span
                        data-testid="no-google-ads-tag"
                        className="shrink-0 text-xs text-muted-foreground"
                      >
                        No Google Ads
                      </span>
                    ) : null}
                    {isCurrentSingle && <Check className="size-4 shrink-0 text-coral" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          <DropdownMenuSeparator />
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-coral transition-colors hover:bg-muted/60"
            onClick={() => {
              setSearchOpen(false);
              setWorkspaceQuery('');
              navigate('/create-workspace');
            }}
          >
            <Plus className="size-4" />
            <span>Create workspace</span>
          </button>
          <DropdownMenuSeparator />
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60"
            onClick={handleSignOut}
          >
            <LogOut className="size-4" />
            <span>Sign out</span>
          </button>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
